import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { DataGrid } from "@mui/x-data-grid";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";

const CareersApplicants = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const token = localStorage.getItem("accessToken");

  const fetchApplicants = async () => {
    try {
      setLoading(true);
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_API}admin/get-job-applicants`,
        headers: { "x-access-token": token },
        data: { page: 1, limit: 1000 },
      });
      setRows(response?.data?.data?.rows || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      Swal.fire({
        title: error?.response?.data?.message || "Failed to load applicants",
        icon: "error",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
    }
  };

  useEffect(() => {
    fetchApplicants();
  }, []);

  // Resumes are private now (no public URL). Fetch the PDF through the
  // admin-authenticated endpoint and open it in a new tab, instead of linking a
  // world-readable blob URL.
  const downloadResume = async (id) => {
    try {
      const response = await axios({
        method: "get",
        url: `${process.env.REACT_APP_API}admin/job-applicant-resume/${id}`,
        headers: { "x-access-token": token },
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      // Anchor-with-download rather than window.open(): a programmatic
      // window.open() after an await is gated by the popup blocker (and blocked
      // outright on Safari), which fails silently. A download anchor is not
      // popup-gated and always works.
      const a = document.createElement("a");
      a.href = url;
      a.download = `resume-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // responseType:'blob' means the server's JSON error body arrives as a Blob,
      // so read it back to surface the real message instead of a bare generic one.
      let message = "Could not load resume";
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          message = JSON.parse(text)?.message || message;
        } else if (error?.response?.data?.message) {
          message = error.response.data.message;
        }
      } catch (_) {
        /* keep the generic message */
      }
      Swal.fire({
        title: message,
        icon: "error",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "candidate_name",
      headerName: "Candidate",
      flex: 1,
      minWidth: 160,
    },
    { field: "position", headerName: "Position", flex: 1, minWidth: 160 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 200 },
    { field: "phone", headerName: "Phone", flex: 1, minWidth: 140 },
    {
      field: "file_path",
      headerName: "Resume",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        params.value ? (
          <button
            type="button"
            onClick={() => downloadResume(params.row.id)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "#1976d2",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Download
          </button>
        ) : (
          "Not provided"
        ),
    },
    {
      field: "createdAt",
      headerName: "Applied",
      flex: 1,
      minWidth: 130,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "",
    },
  ];

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Applicants" />
      <div className="datatable">
        <div className="datatableTitle">
          <Link to="/careers" className="link">
            Back to Careers
          </Link>
        </div>
        <DataGrid
          className="datagrid"
          autoHeight
          rows={rows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 30, 50, 100]}
          pagination
          getRowId={(row) => row.id}
        />
      </div>
    </PageShell>
  );
};

export default CareersApplicants;
